import { EMPTY, of, concat } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import Global from '../../../store/global-selectors';
import { calculatePagingStats } from '../../../services/pagination-service';
import get from 'lodash.get';
import * as ShipTogetherActions from '../../../store/ship-together/ship-together.actions';
import { proceedWithShipTogether$ } from '../../ship-together/ship-together.observables';
import * as PagingActions from '../../../store/paging/paging.actions';
import * as OrderActions from '../../../store/orders/selected-orders/orders.selected-orders.actions';

import * as Api from './alerts.grid.api';
import * as Actions from './alerts.grid.actions';
import { emptyArray } from 'ui-components/default-props/empty';
import { ShipTogetherHelperService } from '../../../services/ship-together-helper-service';
import { bulkLoadFulfillmentPlans$ } from '../../bulkload/bulkload.observables';

const pagingViewName = 'alerts.shiptogether.grid';

export const alertsShipTogetherFetchEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GET_ORDER_ALERTS_SHIP_TOGETHER_REQUEST, Actions.ALERT_DISMISSAL_SUCCESS),
    mergeMap(() => {
      const pagingStats =
        Global.selectPagingViewPagingStats(getState(), {
          viewName: pagingViewName,
        }) || calculatePagingStats(1, 100, 0);
      const { page, pageSize } = pagingStats;
      return Api.getShiptogetherAlerts(page, pageSize).pipe(
        mergeMap((data) => {
          const { totalCount, pageData } = data;
          const actions = [Actions.getOrderAlertsShipTogetherSuccess(pageData)];
          const viewStats = Global.selectPagingViewPagingStats(getState(), {
            viewName: pagingViewName,
          });
          if (viewStats) {
            actions.push(PagingActions.setTotalRecords(pagingViewName, totalCount));
          }
          return of(...actions);
        }),
        catchError((e) => of(Actions.getOrderAlertsShipTogetherFailure(e))),
      );
    }),
  );
};

export const respondToAlertsPageEvents = (action$) => {
  return action$.pipe(
    ofType(Actions.ORDER_ALERTS_GRID_CHANGE_PAGE),
    mergeMap(() => {
      return of(Actions.getOrderAlertsShipTogether());
    }),
  );
};

export const alertsDismissalEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALERT_DISMISSAL_REQUEST),
    mergeMap((action) => {
      const { alertIds, onSuccessHandler } = action;
      const selectedAlertIds = alertIds || Global.selectAlertsGridgetSelectedAlertIds(getState()) || emptyArray;
      if (selectedAlertIds.length === 0) {
        return EMPTY;
      }
      return Api.dismissAlerts(selectedAlertIds).pipe(
        mergeMap(() => {
          if (onSuccessHandler) {
            onSuccessHandler();
          }
          return of(Actions.alertDismissalSuccess());
        }),
        catchError((e) => of(Actions.alertDismissalFailure(e))),
      );
    }),
  );
};

export const getShipTogetherOrdersEpic = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.GET_SHIP_TOGETHER_ORDERS_REQUEST),
    mergeMap(() => {
      const state = getState();
      const [selectedAlertId] = Global.selectAlertsGridgetSelectedAlertIds(state) || emptyArray;
      const alerts = Global.selectAlertsGridAlerts(state);
      const targetAlert = alerts.find((alert) => get(alert, 'alertId') === selectedAlertId);
      const fulfillmentPlanIds = targetAlert?.fulfillmentPlanIds ?? emptyArray;
      return bulkLoadFulfillmentPlans$(fulfillmentPlanIds).pipe(
        mergeMap(() => {
          const orders = ShipTogetherHelperService.getShipTogetherSelectedOrders(state, fulfillmentPlanIds);
          return concat(of(Actions.getShipTogetherOrdersSuccess(orders)), of(Actions.shipOrdersTogetherFromAlerts()));
        }),
        catchError(() => EMPTY),
      ); // TODO: handle error case
    }),
  );

export const shipTogetherFromAlertsEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SHIP_ORDERS_TOGETHER_FROM_ALERTS),
    mergeMap(() => {
      // this returns an array of { salesOrder: object, fulfillmentPlans: object[] }
      const selectedOrderIds = Global.selectSelectedOrdersIds(getState());
      const alertGridOrders = Global.selectAlertsGridOrdersForShipTogether(getState()) || emptyArray;
      const selectedOrders = alertGridOrders.filter((order) => {
        return selectedOrderIds.includes(order.fulfillmentPlans[0].fulfillmentPlanId);
      });
      const selectedFulfillmentPlans = [];
      const selectedSalesOrders = [];
      selectedOrders.forEach(({ fulfillmentPlans, salesOrder }) => {
        selectedFulfillmentPlans.push(...fulfillmentPlans);
        selectedSalesOrders.push(salesOrder);
      });

      return proceedWithShipTogether$(action$, selectedFulfillmentPlans, selectedSalesOrders);
    }),
  );
};

export const shipTogetherSuccessNotificationEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(ShipTogetherActions.SHIP_SALES_ORDERS_TOGETHER_SUCCESS),
    mergeMap((action) => {
      const { shipTogetherItems, fulfillmentPlanIdsWithoutItems } = action ?? {};
      const [selectedAlertId] = Global.selectAlertsGridgetSelectedAlertIds(getState()) || emptyArray;
      const alert = (Global.selectAlertsGridAlerts(getState()) || emptyArray).find((thisAlerts) => thisAlerts.alertId === selectedAlertId);
      const { fulfillmentPlanIds } = alert ?? {};
      const uniqueAlertFulfillmentPlanIds = new Set(fulfillmentPlanIds);
      const uniqueFulfillmentPlanIds = new Set([
        ...shipTogetherItems.map((item) => get(item, 'fulfillmentPlanId')),
        ...fulfillmentPlanIdsWithoutItems,
      ]);
      if (uniqueAlertFulfillmentPlanIds.size - uniqueFulfillmentPlanIds.size <= 1) {
        return of(Actions.alertDismissalRequest(selectedAlertId));
      }
      return EMPTY;
    }),
  );
};

export const getOrdersForSelectedAlertsEpic = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.GET_ORDERS_FOR_SELECTED_ALERTS_REQUEST),
    mergeMap(() => {
      const state = getState();
      const selectedAlertIds = Global.selectAlertsGridgetSelectedAlertIds(state) || emptyArray;
      if (selectedAlertIds.length === 0) {
        return EMPTY;
      }
      const alerts = Global.selectAlertsGridAlerts(state);
      const targetAlerts = alerts.filter((alert) => selectedAlertIds.includes(get(alert, 'alertId')));
      const uniqueFulfillmentPlanIds = [
        ...new Set(targetAlerts.reduce((arr, item) => [...arr, ...get(item, 'fulfillmentPlanIds')], emptyArray)),
      ];

      return bulkLoadFulfillmentPlans$(uniqueFulfillmentPlanIds).pipe(
        mergeMap(() => {
          const orders = ShipTogetherHelperService.getShipTogetherSelectedOrders(state, uniqueFulfillmentPlanIds);
          return concat(
            of(Actions.getOrdersForSelectedAlertsSuccess(orders)),
            of(OrderActions.setSelectedOrders(uniqueFulfillmentPlanIds)),
          );
        }),
        catchError(() => EMPTY),
      ); // TODO: handle error case
    }),
  );

export const getOrdersForAlertSelectionEpic = (action$) =>
  action$.pipe(
    ofType(Actions.SET_SELECTED_ALERT_IDS),
    mergeMap((action) => {
      const selectedIds = get(action, 'selectedIds') || emptyArray;
      if (selectedIds.length === 0) {
        return EMPTY;
      }
      return of(Actions.getOrdersForSelectedAlertsRequest());
    }),
  );
